import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { StatusBar } from "../components/StatusBar";
import { LiveReadout } from "../components/LiveReadout";
import { Compass } from "../components/Compass";
import { TS12Model } from "../components/TS12Model";
import { Joystick } from "../components/Joystick";
import { MeasurePanel } from "../components/MeasurePanel";
import { AtrPanel } from "../components/AtrPanel";
import { PointList } from "../components/PointList";
import { StakeoutPanel } from "../components/StakeoutPanel";
import { NewJobDialog } from "../components/NewJobDialog";
import { SettingsDialog } from "../components/SettingsDialog";
import { SpoolPanel } from "../components/SpoolPanel";
import { StructuralPanel } from "../components/StructuralPanel";
import { useStationState } from "../lib/useStation";
import { station, jobs } from "../lib/api";

export default function Dashboard({ forceTab = null, hideHeader = false } = {}) {
  const { state, error } = useStationState(300);
  const [jobList, setJobList] = useState([]);
  const [job, setJob] = useState(null);
  const [points, setPoints] = useState([]);
  const [showNewJob, setShowNewJob] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [tab, setTab] = useState(forceTab || "survey"); // survey | stakeout

  const connected = state?.connected;
  const disabledCommands = !connected;

  // Load jobs on mount, pick most recent
  useEffect(() => {
    (async () => {
      try {
        const list = await jobs.list();
        setJobList(list);
        if (list.length && !job) selectJob(list[0]);
      } catch (e) {
        /* ignore */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectJob = async (j) => {
    setJob(j);
    try {
      const pts = await jobs.points(j.id);
      setPoints(pts);
    } catch (e) {
      setPoints([]);
    }
  };

  const createJob = async (payload) => {
    const j = await jobs.create(payload);
    setJobList((prev) => [j, ...prev]);
    setJob(j);
    setPoints([]);
    toast.success(`Job "${j.name}" created`);
  };

  const capturePoint = async (payload) => {
    const p = await jobs.addPoint(job.id, payload);
    setPoints((prev) => [...prev, p]);
  };

  const deletePoint = async (id) => {
    await jobs.removePoint(id);
    setPoints((prev) => prev.filter((p) => p.id !== id));
  };

  const toggleConnect = async () => {
    try {
      if (connected) await station.disconnect();
      else await station.connect();
    } catch (e) {
      toast.error("Connection error");
    }
  };

  const hasLink = !!state;

  const jobSelector = useMemo(
    () => (
      <select
        data-testid="job-selector"
        className="rounded-sm border border-[var(--border)] bg-[var(--surface-2)] px-2 py-1 text-xs text-[var(--text)]"
        value={job?.id ?? ""}
        onChange={(e) => {
          const j = jobList.find((x) => x.id === e.target.value);
          if (j) selectJob(j);
        }}
      >
        <option value="" disabled>
          — Select job —
        </option>
        {jobList.map((j) => (
          <option key={j.id} value={j.id}>
            {j.name}
          </option>
        ))}
      </select>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobList, job]
  );

  return (
    <div className="App min-h-screen grid-bg">
      {!hideHeader && (
        <StatusBar
          state={state}
          onToggleConnect={toggleConnect}
          onOpenSettings={() => setShowSettings(true)}
        />
      )}

      {!hasLink && (
        <div className="border-b border-[var(--danger)]/40 bg-[var(--danger)]/10 px-5 py-2 text-xs tracking-widest text-[var(--danger)]">
          NO LINK TO CONTROLLER BACKEND — {error || "connecting…"}
        </div>
      )}

      {/* Tab bar */}
      <div className="flex items-center justify-between border-b border-[var(--border)] bg-[var(--surface)] px-5 py-2">
        <div className="flex items-center gap-2">
          <TabBtn active={tab === "survey"} onClick={() => setTab("survey")} testid="tab-survey">
            Survey
          </TabBtn>
          <TabBtn active={tab === "stakeout"} onClick={() => setTab("stakeout")} testid="tab-stakeout">
            Stakeout
          </TabBtn>
          <TabBtn active={tab === "spool"} onClick={() => setTab("spool")} testid="tab-spool">
            Spool
          </TabBtn>
          <TabBtn active={tab === "structural"} onClick={() => setTab("structural")} testid="tab-structural">
            Structural
          </TabBtn>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">Job</span>
          {jobSelector}
        </div>
      </div>

      <main className={(tab === "spool" || tab === "structural")
        ? "grid gap-3 p-3"
        : "grid gap-3 p-3 lg:grid-cols-12 lg:grid-rows-[auto_1fr]"}>
        {tab !== "spool" && tab !== "structural" && (
          <>
            <div className="lg:col-span-5 lg:row-span-1">
              <LiveReadout state={state} />
            </div>
            <div className="lg:col-span-3">
              <TS12Model state={state} />
            </div>
            <div className="flex flex-col gap-3 lg:col-span-4">
              <MeasurePanel disabled={disabledCommands} />
              <AtrPanel state={state} disabled={disabledCommands} />
            </div>
            <div className="lg:col-span-4">
              <Joystick disabled={disabledCommands} />
            </div>
          </>
        )}

        <div className={(tab === "spool" || tab === "structural") ? "" : "lg:col-span-8 min-h-[280px]"}>
          {tab === "survey" ? (
            <PointList
              job={job}
              points={points}
              state={state}
              onCapture={capturePoint}
              onDelete={deletePoint}
              onNewJob={() => setShowNewJob(true)}
            />
          ) : tab === "stakeout" ? (
            <StakeoutPanel disabled={disabledCommands} />
          ) : tab === "spool" ? (
            <SpoolPanel disabled={disabledCommands} />
          ) : (
            <StructuralPanel state={state} disabled={disabledCommands} />
          )}
        </div>
      </main>

      <NewJobDialog open={showNewJob} onOpenChange={setShowNewJob} onCreate={createJob} />
      <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`rounded-sm px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
      active
        ? "bg-[var(--neon)] text-black"
        : "border border-[var(--border)] text-[var(--text-dim)] hover:border-[var(--neon)] hover:text-[var(--neon)]"
    }`}
  >
    {children}
  </button>
);
